import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function GujaratiGreeting() {
  const [stage, setStage] = useState('fullscreen'); // fullscreen -> minimized -> hidden
  const [dismissed, setDismissed] = useState(false);
  const [festivalGreeting, setFestivalGreeting] = useState(null);

  useEffect(() => {
    // Fetch festival greeting (e.g. Shubh Diwali / Nutan Varsha Abhinandan / Happy Holi / Happy Dhuleti)
    fetch(`${BACKEND_URL}/api/public/settings`)
      .then(r => r.json())
      .then(s => setFestivalGreeting(s.festival_greeting || null))
      .catch(() => {});
    // Show fullscreen for 2.5 seconds, then minimize on EVERY page load
    const timer = setTimeout(() => {
      setStage('minimized');
    }, 2500);

    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    setTimeout(() => setStage('hidden'), 300);
  };

  if (stage === 'hidden') return null;
  if (/^\/(admin|claim|verify|payment\/success)/.test(window.location.pathname)) return null;   // staff portal, claim/verification forms, and the ticket confirmation page: no greeting overlay

  return (
    <AnimatePresence>
      {stage === 'fullscreen' && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
          className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none"
          data-testid="gujarati-greeting-overlay"
        >
          {/* 50% Transparent Blurred Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-[hsl(var(--gs-navy))]/50 backdrop-blur-xl"
            style={{
              backdropFilter: 'blur(24px) saturate(180%)',
              WebkitBackdropFilter: 'blur(24px) saturate(180%)',
            }}
          />

          {/* Greeting Text with Zoom Animation */}
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 1.2, opacity: 0 }}
            transition={{ duration: 0.8, ease: [0.34, 1.56, 0.64, 1] }}
            className="relative z-10 text-center px-4"
          >
            {/* Decorative Elements */}
            <motion.div
              className="absolute -top-16 left-1/2 -translate-x-1/2 w-32 h-32 bg-[hsl(var(--gs-accent-gold))] rounded-full opacity-20 blur-3xl"
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.2, 0.3, 0.2],
              }}
              transition={{ duration: 2, repeat: Infinity }}
            />

            {/* Indian Flag */}
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="text-8xl mb-6"
            >
              🇮🇳
            </motion.div>

            {/* Main Gujarati Text */}
            <h1
              className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-white drop-shadow-2xl"
              style={{
                fontFamily: 'Space Grotesk',
                textShadow: '0 0 40px rgba(0,71,151,0.5), 0 0 80px rgba(0,71,151,0.3)',
                letterSpacing: '0.02em',
              }}
            >
              જય જય ગરવી ગુજરાત!
            </h1>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold text-white/90 mt-4 drop-shadow-xl"
              style={{
                fontFamily: 'Space Grotesk',
                textShadow: '0 0 30px rgba(0,71,151,0.4)',
              }}
            >
              {festivalGreeting || 'જય હિંદ! 🇮🇳'}
            </motion.h2>

            {/* Decorative Bottom Glow */}
            <motion.div
              className="absolute -bottom-16 left-1/2 -translate-x-1/2 w-64 h-16 bg-white/10 rounded-full blur-2xl"
              animate={{
                scaleX: [1, 1.5, 1],
                opacity: [0.1, 0.2, 0.1],
              }}
              transition={{ duration: 2.5, repeat: Infinity }}
            />
          </motion.div>
        </motion.div>
      )}

      {stage === 'minimized' && !dismissed && (
        <motion.div
          initial={{ opacity: 0, x: -100, scale: 0.8 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={{ opacity: 0, x: -100, scale: 0.8 }}
          transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
          className="relative group pointer-events-auto"
          data-testid="gujarati-greeting-minimized"
        >
          <div className="relative">
            {/* Backdrop Blur Card */}
            <div
              className="bg-[hsl(var(--gs-blue))]/20 backdrop-blur-lg border border-white/20 rounded-2xl px-6 py-3 shadow-2xl"
              style={{
                backdropFilter: 'blur(16px) saturate(180%)',
                WebkitBackdropFilter: 'blur(16px) saturate(180%)',
              }}
            >
              <p
                className="text-white font-semibold text-sm sm:text-base drop-shadow-md"
                style={{ fontFamily: 'Space Grotesk' }}
              >
                જય જય ગરવી ગુજરાત! 🇮🇳
              </p>
              {festivalGreeting && (
                <p className="text-white/90 font-medium text-xs sm:text-sm mt-0.5" style={{ fontFamily: 'Space Grotesk' }} data-testid="greeting-festival-line">
                  {festivalGreeting}
                </p>
              )}
            </div>

            {/* Dismiss Button */}
            <button
              onClick={handleDismiss}
              className="absolute -top-2 -right-2 w-6 h-6 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2"
              aria-label="Dismiss greeting"
              data-testid="greeting-dismiss-button"
            >
              <X className="w-3.5 h-3.5 text-[hsl(var(--gs-navy))]" />
            </button>

            {/* Floating Animation */}
            <motion.div
              className="absolute inset-0 rounded-2xl bg-gradient-to-r from-[hsl(var(--gs-blue))]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
              animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
